/**
 * AnnounceCohortModal (MED-604, MED-645 E18-16)
 *
 * Single-view modal for announcing cohort modalities to participants.
 * Top section: Readiness check — prerequisite validation per modality
 * Bottom section: Email editor — merge tags, subject, plain text body, expandable preview
 * Matches the intake form email pattern (ComposeIntakeEmailModal).
 */

import { useCallback, useEffect, useState } from 'react';
import {
  Modal,
  Button,
  Stack,
  Text,
  Group,
  Alert,
  List,
  Loader,
  Center,
  Divider,
  ThemeIcon,
  Box,
  Badge,
  TextInput,
  Textarea,
  Paper,
  Tooltip,
} from '@mantine/core';
import {
  AlertCircle,
  CheckCircle,
  Eye,
  Tag,
  AlertTriangle,
} from 'react-feather';
import { notifications } from '@mantine/notifications';
import { cohortsApi } from '../../../../services/api/cohorts';
import { useInsertAtCursor } from '../../../../hooks';
import type { ModalityType } from '../../../../types';

// ── Merge Tag Types ─────────────────────────────────────────────────────────────

interface MergeTagDefinition {
  tag: string;
  label: string;
  category: 'participant' | 'partner' | 'cohort_config' | 'system';
  description: string;
}

interface ModalityPrerequisiteStatus {
  modalityId: string;
  modalityType: ModalityType;
  label: string;
  ready: boolean;
  errors: string[];
}

// ── Category Labels ─────────────────────────────────────────────────────────────

const CATEGORY_LABELS: Record<string, string> = {
  participant: 'Participant',
  partner: 'Partner',
  cohort_config: 'Cohort Config',
  system: 'System',
};

const CATEGORY_ORDER = ['participant', 'partner', 'cohort_config', 'system'];

// ── Main Modal ──────────────────────────────────────────────────────────────────

interface AnnounceCohortModalProps {
  opened: boolean;
  onClose: () => void;
  cohortId: string;
  onSuccess: (announcedModalityIds: string[]) => void;
  emailDisabled?: boolean;
}

export function AnnounceCohortModal({
  opened,
  onClose,
  cohortId,
  onSuccess,
  emailDisabled,
}: AnnounceCohortModalProps) {
  // Readiness
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [modalities, setModalities] = useState<ModalityPrerequisiteStatus[]>([]);
  const [readyCount, setReadyCount] = useState(0);

  // Email editor
  const [emailSubject, setEmailSubject] = useState('');
  const [emailBody, setEmailBody] = useState('');
  const [mergeTagDefinitions, setMergeTagDefinitions] = useState<MergeTagDefinition[]>([]);
  const [hasEdited, setHasEdited] = useState(false);

  // Cursor tracking for merge tag insertion (intake form pattern)
  const [lastFocusedField, setLastFocusedField] = useState<'subject' | 'body'>('body');
  const subjectField = useInsertAtCursor<HTMLInputElement>();
  const bodyField = useInsertAtCursor<HTMLTextAreaElement>();

  // Preview
  const [showPreview, setShowPreview] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewSubject, setPreviewSubject] = useState('');
  const [previewBody, setPreviewBody] = useState('');
  const [unresolvableTags, setUnresolvableTags] = useState<string[]>([]);
  const [sampleParticipant, setSampleParticipant] = useState<{
    first_name: string;
    email: string;
  }>({ first_name: '', email: '' });
  const [recipientCount, setRecipientCount] = useState(0);

  // Test email
  const [sendingTest, setSendingTest] = useState(false);

  // Confirmation dialog (MED-646)
  const [confirmOpened, setConfirmOpened] = useState(false);

  // ── Reset on open ──────────────────────────────────────────────────────────

  useEffect(() => {
    if (!opened) return;
    setModalities([]);
    setLoading(true);
    setHasEdited(false);
    setEmailSubject('');
    setEmailBody('');
    setShowPreview(false);
    setConfirmOpened(false);
    setUnresolvableTags([]);

    // Load readiness check and email template in parallel
    Promise.all([
      cohortsApi.getAnnounceAllPrerequisites(cohortId),
      cohortsApi.getAnnouncementEmailTemplate(cohortId),
    ])
      .then(([prereqs, template]) => {
        setModalities(prereqs.modalities);
        setReadyCount(prereqs.readyCount);
        setEmailSubject(template.subject);
        setEmailBody(template.body);
        setMergeTagDefinitions(template.mergeTagDefinitions);
      })
      .catch(() => {
        setModalities([]);
      })
      .finally(() => setLoading(false));
  }, [opened, cohortId]);

  // ── Load preview ───────────────────────────────────────────────────────────

  const loadPreview = useCallback(async () => {
    setPreviewLoading(true);
    try {
      const preview = await cohortsApi.previewAnnouncementEmail(cohortId, emailSubject, emailBody);
      setPreviewSubject(preview.subject);
      setPreviewBody(preview.body);
      setUnresolvableTags(preview.unresolvableTags);
      setSampleParticipant(preview.sampleParticipant);
      setRecipientCount(preview.recipientCount);
    } catch {
      notifications.show({
        title: 'Error',
        message: 'Failed to generate preview',
        color: 'red',
      });
    } finally {
      setPreviewLoading(false);
    }
  }, [cohortId, emailSubject, emailBody]);

  // ── Handlers ───────────────────────────────────────────────────────────────

  const readyModalities = modalities.filter((m) => m.ready);
  const notReadyModalities = modalities.filter((m) => !m.ready);

  const handleAnnounceWithoutEmails = async () => {
    setSubmitting(true);
    try {
      const modalityIds = readyModalities.map((m) => m.modalityId);
      const result = await cohortsApi.announceAllModalities(cohortId, false, modalityIds);
      notifications.show({
        title: 'Program Announced',
        message: `${result.announcedModalities.length} modality type${result.announcedModalities.length !== 1 ? 's' : ''} announced. Invitation emails were skipped.`,
        color: 'green',
      });
      onSuccess(result.announcedModalities.map((m) => m.id));
      onClose();
    } catch (error) {
      notifications.show({
        title: 'Announcement Failed',
        message: error instanceof Error ? error.message : 'Please try again.',
        color: 'red',
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleAnnounceAndSendEmails = async () => {
    setSubmitting(true);
    try {
      const modalityIds = readyModalities.map((m) => m.modalityId);
      // sendInvites=false here suppresses the legacy "Your program is ready"
      // email; the customizable announcement below replaces it for this flow.
      const announceResult = await cohortsApi.announceAllModalities(cohortId, false, modalityIds);
      const emailResult = await cohortsApi.sendAnnouncementEmails(
        cohortId,
        emailSubject,
        emailBody,
      );

      notifications.show({
        title: 'Program Announced',
        message: `${announceResult.announcedModalities.length} modality type${announceResult.announcedModalities.length !== 1 ? 's' : ''} announced and ${emailResult.emailsSent} announcement email${emailResult.emailsSent !== 1 ? 's' : ''} sent.`,
        color: 'green',
      });

      onSuccess(announceResult.announcedModalities.map((m) => m.id));
      onClose();
    } catch (error) {
      notifications.show({
        title: 'Send Failed',
        message: error instanceof Error ? error.message : 'Please try again.',
        color: 'red',
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleSendTestEmail = async () => {
    setSendingTest(true);
    try {
      const result = await cohortsApi.sendTestAnnouncementEmail(
        cohortId,
        emailSubject,
        emailBody,
      );
      notifications.show({
        title: 'Test Email Sent',
        message: `Test email sent to ${result.email}`,
        color: 'green',
      });
    } catch (error) {
      notifications.show({
        title: 'Error',
        message: error instanceof Error ? error.message : 'Failed to send test email',
        color: 'red',
      });
    } finally {
      setSendingTest(false);
    }
  };

  const handleClose = () => {
    if (hasEdited) {
      const confirmed = window.confirm(
        'You have unsaved changes to the email template. Are you sure you want to close?',
      );
      if (!confirmed) return;
    }
    onClose();
  };

  const handleInsertTag = (tag: string) => {
    if (lastFocusedField === 'subject') {
      subjectField.insert(tag, () => emailSubject, setEmailSubject);
    } else {
      bodyField.insert(tag, () => emailBody, setEmailBody);
    }
    setHasEdited(true);
  };

  const handleTogglePreview = async () => {
    if (!showPreview) {
      setShowPreview(true);
      await loadPreview();
    } else {
      setShowPreview(false);
    }
  };

  // Group merge tags by category
  const groupedTags = CATEGORY_ORDER.reduce(
    (acc, cat) => {
      const catTags = mergeTagDefinitions.filter((t) => t.category === cat);
      if (catTags.length > 0) acc[cat] = catTags;
      return acc;
    },
    {} as Record<string, MergeTagDefinition[]>,
  );

  const emailReady = emailSubject.trim().length > 0 && emailBody.trim().length > 0;

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <>
      <Modal
        opened={opened}
        onClose={handleClose}
        title="Finalize & Announce to Participants"
        size="xl"
      >
        {loading ? (
          <Center py="xl">
            <Loader size="sm" />
          </Center>
        ) : modalities.length === 0 ? (
          <Stack gap="md">
            <Text size="sm" c="dimmed">
              No unannounced modalities found for this cohort.
            </Text>
            <Group justify="flex-end">
              <Button variant="default" onClick={onClose}>
                Close
              </Button>
            </Group>
          </Stack>
        ) : (
          <Stack gap="md">
            {/* ── Readiness Check Section ───────────────────────────────── */}
            <Text size="sm" c="dimmed">
              The following modalities will be announced to participants:
            </Text>

            <Stack gap="xs">
              {modalities.map((m) => (
                <Box
                  key={m.modalityId}
                  style={{
                    border: '1px solid #E8E6E1',
                    borderRadius: 8,
                    padding: '12px 16px',
                    backgroundColor: m.ready ? '#FAFAF8' : '#FFF5F5',
                  }}
                >
                  <Group justify="space-between" align="center">
                    <Group gap="sm">
                      {m.ready ? (
                        <ThemeIcon color="green" variant="light" size="sm" radius="xl">
                          <CheckCircle size={12} />
                        </ThemeIcon>
                      ) : (
                        <ThemeIcon color="red" variant="light" size="sm" radius="xl">
                          <AlertCircle size={12} />
                        </ThemeIcon>
                      )}
                      <Text size="sm" fw={600}>
                        {m.label}
                      </Text>
                    </Group>
                    <Badge color={m.ready ? 'green' : 'red'} variant="light" size="sm">
                      {m.ready ? 'Ready' : 'Not Ready'}
                    </Badge>
                  </Group>
                  {!m.ready && m.errors.length > 0 && (
                    <List size="xs" mt="xs" spacing={2} c="red">
                      {m.errors.map((err, i) => (
                        <List.Item key={i}>{err}</List.Item>
                      ))}
                    </List>
                  )}
                </Box>
              ))}
            </Stack>

            {notReadyModalities.length > 0 && readyCount > 0 && (
              <Alert icon={<AlertCircle size={16} />} color="yellow" variant="light">
                <Text size="sm">
                  {notReadyModalities.length} modality
                  {notReadyModalities.length !== 1 ? ' types are' : ' type is'} not ready and will
                  be skipped.
                </Text>
              </Alert>
            )}

            {readyCount === 0 && (
              <Alert
                icon={<AlertCircle size={16} />}
                title="No Modalities Ready"
                color="red"
                variant="light"
              >
                <Text size="sm">
                  All modalities have unresolved prerequisites. Please resolve the issues listed
                  above before announcing.
                </Text>
              </Alert>
            )}

            {/* ── Email Content Section (below divider) ─────────────────── */}
            {readyCount > 0 && (
              <>
                <Divider label="Email Content" labelPosition="left" />

                {/* Merge Tag Buttons */}
                <Box>
                  <Group gap="xs" mb={8}>
                    <Tag size={14} />
                    <Text size="sm" fw={500}>
                      Insert Merge Tag
                    </Text>
                  </Group>
                  <Stack gap="xs">
                    {Object.entries(groupedTags).map(([category, catTags]) => (
                      <Box key={category}>
                        <Text size="xs" c="dimmed" mb={4}>
                          {CATEGORY_LABELS[category]}
                        </Text>
                        <Group gap={4}>
                          {catTags.map((mergeTag) => {
                            const isUnresolvable = unresolvableTags.includes(mergeTag.tag);
                            return (
                              <Tooltip
                                key={mergeTag.tag}
                                label={mergeTag.description}
                                position="top"
                                withArrow
                              >
                                <Badge
                                  variant="light"
                                  color={isUnresolvable ? 'yellow' : 'gray'}
                                  style={{
                                    cursor: 'pointer',
                                    border: isUnresolvable
                                      ? '1px dashed var(--mantine-color-yellow-5)'
                                      : undefined,
                                  }}
                                  onClick={() => handleInsertTag(mergeTag.tag)}
                                  rightSection={
                                    isUnresolvable ? (
                                      <AlertTriangle
                                        size={10}
                                        color="var(--mantine-color-yellow-7)"
                                      />
                                    ) : null
                                  }
                                >
                                  {mergeTag.label}
                                </Badge>
                              </Tooltip>
                            );
                          })}
                        </Group>
                      </Box>
                    ))}
                  </Stack>
                </Box>

                {/* Subject */}
                <TextInput
                  ref={subjectField.ref}
                  label="Subject"
                  placeholder="Enter email subject"
                  value={emailSubject}
                  onChange={(e) => {
                    setEmailSubject(e.currentTarget.value);
                    setHasEdited(true);
                  }}
                  onFocus={() => setLastFocusedField('subject')}
                  onSelect={subjectField.trackCursor}
                />

                {/* Message Body */}
                <Textarea
                  ref={bodyField.ref}
                  label="Message Body"
                  placeholder="Enter email message"
                  value={emailBody}
                  onChange={(e) => {
                    setEmailBody(e.currentTarget.value);
                    setHasEdited(true);
                  }}
                  onFocus={() => setLastFocusedField('body')}
                  onSelect={bodyField.trackCursor}
                  minRows={10}
                  autosize
                  maxRows={16}
                />

                {/* Preview Toggle */}
                <Group justify="flex-end">
                  <Button
                    variant="subtle"
                    size="xs"
                    leftSection={<Eye size={14} />}
                    onClick={handleTogglePreview}
                    loading={previewLoading}
                  >
                    {showPreview ? 'Hide Preview' : 'Show Preview'}
                  </Button>
                </Group>

                {/* Preview Section */}
                {showPreview && !previewLoading && (
                  <Paper withBorder p={0} bg="gray.1" style={{ overflow: 'hidden' }}>
                    {/* Preview Header */}
                    <Box p="xs" style={{ borderBottom: '1px solid var(--mantine-color-gray-3)' }}>
                      <Text size="xs" c="dimmed" tt="uppercase" fw={500}>
                        Preview (as seen by {sampleParticipant.first_name || 'Participant'})
                      </Text>
                    </Box>

                    {/* Unresolvable tags warning */}
                    {unresolvableTags.length > 0 && (
                      <Box p="xs">
                        <Alert
                          icon={<AlertTriangle size={14} />}
                          color="yellow"
                          variant="light"
                          p="xs"
                        >
                          <Text size="xs">
                            {unresolvableTags.length} merge tag
                            {unresolvableTags.length !== 1 ? 's' : ''} could not be resolved:{' '}
                            <Text component="span" fw={600} ff="monospace" size="xs">
                              {unresolvableTags.join(', ')}
                            </Text>
                          </Text>
                        </Alert>
                      </Box>
                    )}

                    {/* Email Metadata */}
                    <Box
                      p="md"
                      bg="white"
                      style={{ borderBottom: '1px solid var(--mantine-color-gray-2)' }}
                    >
                      <Text size="sm" c="dimmed">
                        From:{' '}
                        <Text span fw={600} c="dark">
                          Medley SGOS
                        </Text>{' '}
                        &lt;notifications@sgos.withmedley.com&gt;
                      </Text>
                      <Text size="sm" fw={600} mt={4}>
                        {previewSubject}
                      </Text>
                    </Box>

                    {/* Email Body Preview */}
                    <Box p="md" bg="white">
                      <Text
                        size="sm"
                        style={{ whiteSpace: 'pre-wrap', lineHeight: 1.6 }}
                      >
                        {previewBody}
                      </Text>
                    </Box>

                    {/* Email Footer */}
                    <Box
                      p="sm"
                      bg="gray.1"
                      style={{ borderTop: '1px solid var(--mantine-color-gray-2)' }}
                    >
                      <Text size="xs" c="dimmed" ta="center">
                        This email was sent to {sampleParticipant.email || 'participant@example.com'}
                      </Text>
                    </Box>
                  </Paper>
                )}

                <Divider />

                {/* MED-579: Email disabled notice */}
                {emailDisabled && (
                  <Alert icon={<AlertTriangle size={16} />} color="yellow" variant="light">
                    <Text size="sm">
                      Announcement emails have been turned off for this cohort. You can still announce
                      without sending emails, or copy the email template for manual sending.
                    </Text>
                  </Alert>
                )}

                {/* ── CTAs ───────────────────────────────────────────────── */}
                <Group justify="flex-end" gap="sm">
                  <Button variant="default" onClick={handleClose} disabled={submitting}>
                    Cancel
                  </Button>
                  {emailDisabled && (
                    <Button
                      variant="outline"
                      onClick={() => {
                        const template = `Subject: ${emailSubject}\n\n${emailBody}`;
                        navigator.clipboard.writeText(template);
                        notifications.show({
                          title: 'Copied',
                          message: 'Email template copied to clipboard',
                          color: 'green',
                        });
                      }}
                      disabled={!emailReady}
                    >
                      Copy Email Template
                    </Button>
                  )}
                  {!emailDisabled && (
                    <Button
                      variant="outline"
                      onClick={handleSendTestEmail}
                      loading={sendingTest}
                      disabled={!emailReady || submitting}
                    >
                      Send Test Email
                    </Button>
                  )}
                  <Button
                    variant={emailDisabled ? undefined : 'outline'}
                    color={emailDisabled ? 'medleyGreen' : undefined}
                    onClick={handleAnnounceWithoutEmails}
                    loading={submitting && !confirmOpened}
                    disabled={submitting || sendingTest}
                  >
                    Announce Without Sending Emails
                  </Button>
                  {!emailDisabled && (
                    <Button
                      color="medleyGreen"
                      onClick={() => setConfirmOpened(true)}
                      disabled={!emailReady || submitting || sendingTest}
                    >
                      Announce &amp; Send Emails
                    </Button>
                  )}
                </Group>
              </>
            )}

            {readyCount === 0 && (
              <Group justify="flex-end">
                <Button variant="default" onClick={onClose}>
                  Close
                </Button>
              </Group>
            )}
          </Stack>
        )}
      </Modal>

      {/* MED-646: Bulk send confirmation dialog */}
      <Modal
        opened={confirmOpened}
        onClose={() => setConfirmOpened(false)}
        title="Confirm Email Send"
        size="sm"
        centered
        zIndex={301}
      >
        <Stack gap="md">
          <Alert icon={<AlertTriangle size={16} />} color="yellow" variant="light">
            <Text size="sm">
              You are about to announce{' '}
              <Text component="span" fw={700}>
                {readyCount} modality type{readyCount !== 1 ? 's' : ''}
              </Text>{' '}
              and send this email to{' '}
              <Text component="span" fw={700}>
                {recipientCount > 0
                  ? `${recipientCount} participant${recipientCount !== 1 ? 's' : ''}`
                  : 'all active participants'}
              </Text>
              . This action cannot be undone.
            </Text>
          </Alert>
          <Group justify="flex-end" gap="sm">
            <Button
              variant="default"
              onClick={() => setConfirmOpened(false)}
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button
              color="medleyGreen"
              onClick={() => {
                setConfirmOpened(false);
                handleAnnounceAndSendEmails();
              }}
              loading={submitting}
              disabled={submitting}
            >
              Confirm &amp; Send
            </Button>
          </Group>
        </Stack>
      </Modal>
    </>
  );
}
